import { Show, createSignal, onCleanup, onMount } from 'solid-js';
import { formatFingerprint } from '../crypto/wasm-bridge';

type Sev = 'good' | 'warn' | 'crit' | 'neutral';

const STATUS_SEV: Record<string, Sev> = {
  live: 'good',
  paused: 'warn',
  reconnecting: 'warn',
  error: 'crit',
  unreachable: 'crit',
};

const DOT: Record<Sev, string> = {
  good: 'bg-good',
  warn: 'bg-warn',
  crit: 'bg-crit',
  neutral: 'bg-faint',
};

/**
 * HUD topbar. The session fingerprints are no longer permanent furniture: they
 * live behind the status pill, one click away, so the first screenful is data.
 */
export function Header(props: { browserFp: string; agentFp: string; status: string }) {
  const [open, setOpen] = createSignal(false);
  const sev = (): Sev => STATUS_SEV[props.status] ?? 'neutral';

  // Pill + disclosure share this wrapper, so "outside" is just "outside this
  // node" — a click on the pill or inside the panel must not self-close.
  let wrapRef: HTMLDivElement | undefined;

  const onDocClick = (e: MouseEvent) => {
    if (!open()) return;
    if (wrapRef && e.target instanceof Node && !wrapRef.contains(e.target)) {
      setOpen(false);
    }
  };
  const onDocKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') setOpen(false);
  };

  // Listeners live on `document` for the component's whole lifetime (cheaper
  // than attach/detach per open/close) but MUST be removed on unmount —
  // Header instances come and go with pairing state, and a stale listener
  // here would pile up across reconnects in a long-lived dashboard tab.
  onMount(() => {
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onDocKeydown);
  });
  onCleanup(() => {
    document.removeEventListener('click', onDocClick);
    document.removeEventListener('keydown', onDocKeydown);
  });

  return (
    <header class="mx-auto flex w-full max-w-[1180px] items-center justify-between px-5 pb-3 pt-4">
      <div class="flex items-center gap-3">
        <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="2.5" width="2.6" height="19" fill="var(--holo)" opacity="0.9" />
          <rect x="18.4" y="2.5" width="2.6" height="19" fill="var(--holo)" opacity="0.9" />
          <rect x="5.6" y="10.7" width="12.8" height="2.6" fill="var(--holo)" opacity="0.55" />
          <circle cx="14.8" cy="12" r="1.9" fill="var(--amber)" />
        </svg>
        <div>
          <h1 class="hud-mono text-base font-semibold uppercase tracking-[0.22em] text-ink">Heligraph</h1>
          <p class="hud-mono text-[0.5938rem] uppercase tracking-[0.3em] text-faint">Mission control</p>
        </div>
      </div>

      <div class="relative" ref={wrapRef}>
        <button
          type="button"
          aria-expanded={open() ? 'true' : 'false'}
          onClick={() => setOpen(!open())}
          class="hud-clip hud-mono flex min-h-[36px] items-center gap-2 border border-line-strong bg-accent-soft px-4 text-xs uppercase tracking-[0.22em] text-holo"
        >
          <span class={`h-1.5 w-1.5 flex-none rounded-full ${DOT[sev()]}`} />
          {props.status}
        </button>

        <Show when={open()}>
          <div
            role="dialog"
            aria-label="Encrypted session"
            class="hud-clip absolute right-0 top-[calc(100%+10px)] z-30 w-[310px] border border-line-strong bg-bg/95 p-4"
          >
            <h2 class="hud-mono mb-2 text-[0.625rem] uppercase tracking-[0.24em] text-faint">
              Encrypted session
            </h2>
            <p class="flex justify-between gap-3 py-1 text-xs">
              <span class="text-muted">Browser key</span>
              <span class="hud-mono text-holo">{formatFingerprint(props.browserFp).slice(0, 19)}…</span>
            </p>
            <p class="flex justify-between gap-3 py-1 text-xs">
              <span class="text-muted">Agent key</span>
              <span class="hud-mono text-holo">{formatFingerprint(props.agentFp).slice(0, 19)}…</span>
            </p>
            <p class="mt-2 border-t border-line pt-2 text-[0.72rem] text-faint">
              End-to-end encrypted — the relay only forwards ciphertext.
            </p>
          </div>
        </Show>
      </div>
    </header>
  );
}
